import {OverlayState} from "@/hooks/common/useOverlay";
import {useTaggingLoad} from "@/hooks/common/useTagging";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import * as style from "@COMPONENTS/style/productlist/compareproductM";
import {TextButton} from "@FRAMEWORK/ui/mobile/button";
import {PopupContainer} from "@FRAMEWORK/ui/mobile/popup";
import {Table} from "@FRAMEWORK/ui/mobile/table";
import useGoodsCompare from "@QUERIES/prdt/goods/useGoodsCompare";
import {Flag} from "@yb/ui/mobile/common/Flag";
import {format} from "@yb/utils";
import {useRouter} from "next/router";

interface Props {
	selected: string[];
	showDepartureDayPopup: (goodsCd: string) => void;
}

const CompareProductsPopupComponent = ({selected, showDepartureDayPopup, ...overlayProps}: OverlayState & Props) => {
	const {data} = useGoodsCompare(selected.toString());
	const router = useRouter();
	const compareList = data?.body.compareList;

	useTaggingLoad("pkg_compare_goodsCd", compareList);

	const ProductName = () => (
		<>
			<style.FixedArea>
				<th scope="col" colSpan={2}>
					상품명
				</th>
			</style.FixedArea>
			<style.FixedArea>
				{compareList.map((product, index) => (
					<td key={index}>
						<style.Tag>
							<Flag $type="code" text={product.goodsCd} />
						</style.Tag>
						{product.goodsNm}
					</td>
				))}
			</style.FixedArea>
		</>
	);
	const ProductFee = () => (
		<>
			<tr>
				<th scope="col" colSpan={2}>
					상품가
				</th>
			</tr>
			<tr>
				{compareList.map((product, index) => (
					<td key={index}>
						<style.PriceList>
							<style.PriceListItem>
								성인
								<style.Price>
									<span>
										<em>{format.koKRFormatNumber(product.minAdtPrice)}</em>원
									</span>
									~
									<span>
										<em>{format.koKRFormatNumber(product.maxAdtPrice)}</em>원
									</span>
								</style.Price>
							</style.PriceListItem>
							<style.PriceListItem>
								아동
								<style.Price>
									<span>
										<em>{format.koKRFormatNumber(product.minChdPrice)}</em>원
									</span>
									~
									<span>
										<em>{format.koKRFormatNumber(product.maxChdPrice)}</em>원
									</span>
								</style.Price>
							</style.PriceListItem>
							<style.PriceListItem>
								유아
								<style.Price>
									<span>
										<em>{format.koKRFormatNumber(product.minInfPrice)}</em>원
									</span>
									~
									<span>
										<em>{format.koKRFormatNumber(product.maxInfPrice)}</em>원
									</span>
								</style.Price>
							</style.PriceListItem>
						</style.PriceList>
					</td>
				))}
			</tr>
		</>
	);
	const TravelPeriod = () => (
		<>
			<tr>
				<th scope="col" colSpan={2}>
					여행기간
				</th>
			</tr>
			<tr>
				{compareList.map((product, index) => (
					<td key={index}>{product.evNightNm}</td>
				))}
			</tr>
		</>
	);
	const VisitCities = () => (
		<>
			<tr>
				<th scope="col" colSpan={2}>
					방문도시
				</th>
			</tr>
			<tr>
				{compareList.map((product, index) => (
					<td key={index}>{product.vcityInfo}</td>
				))}
			</tr>
		</>
	);
	const TravelPoints = () => (
		<>
			{compareList.some(e => !!e.prdExpl) && (
				<>
					<tr>
						<th scope="col" colSpan={2}>
							여행포인트
						</th>
					</tr>
					<tr>
						{compareList.map((product, index) => (
							<td key={index}>
								<p dangerouslySetInnerHTML={{__html: product.prdExpl?.replace(/\r\n/g, "<br/>")}}></p>
							</td>
						))}
					</tr>
				</>
			)}
		</>
	);

	const CompareActions = () => (
		<>
			<tr>
				{compareList.map((product, index) => (
					<td key={index}>
						<style.BtnBlock>
							<TextButton size="default" text="출발일 보기" onClick={() => showDepartureDayPopup(product.goodsCd)} />
							<TextButton
								size="default"
								types="basic"
								text="상품 상세보기"
								onClick={() => router.push(product.webLinkUrl)}
							/>
						</style.BtnBlock>
					</td>
				))}
			</tr>
		</>
	);

	return (
		<>
			{compareList && (
				<PopupContainer {...overlayProps} title="비교하기" padding={false} size="full" animate="slideIn">
					<style.Wrap>
						<Table>
							<caption>상품 비교하기</caption>
							<colgroup>
								<col style={{width: "50%"}} />
								<col style={{width: "50%"}} />
							</colgroup>
							<tbody>
								<ProductName />
								<ProductFee />
								<TravelPeriod />
								<VisitCities />
								<TravelPoints />
								<CompareActions />
							</tbody>
						</Table>
					</style.Wrap>
				</PopupContainer>
			)}
		</>
	);
};

const CompareProductsPopup = (props: OverlayState & Props) => (
	<ErrorBoundary logMsg="Popup CompareProducts Error">
		<CompareProductsPopupComponent {...props} />
	</ErrorBoundary>
);

export default CompareProductsPopup;
